import api from '@/libs/api'
import { notifyWarning } from '@/libs/notify'
import { DIRECTION_OUT } from '@/constants/directions'

import { ROUTE_ATTENDANCE_ITEM } from '@/router/constants'
import { getChatExpired, hasContactReplied } from '@/libs/helpers'
import { isLid } from '@/components/utils/phones'
import AskTemplateQueue from '@/components/AskTemplateQueue'
import AskTemplateParams from '@/components/AskTemplateParams'

const router = document.router

export const retryStartCall = async (vm, attendance, anonymizationEnabled = false) => {
  if (!attendance) return null

  const item = { ...attendance }
  item.contact = String(attendance?.contact || '').trim()
  if (!item.contact) return

  const isInternal = item.contact.length <= 7
  const isOutDirection = item.channel === 'VOICE' && item.direction === DIRECTION_OUT

  if (isInternal || isOutDirection) {
    const params = anonymizationEnabled
      ? [item.contact, null, anonymizationEnabled]
      : item.contact

    vm.$store.dispatch('sipModule/call', params)
    return item.contact
  }

  const areaCode = vm.$store.state.agentModule?.provision?.areaCode || ''
  const { contact } = await getFormatedNumber(item, areaCode)

  const optionCallback = vm.$store.state.attendanceModule?.optionCallback
  if (optionCallback && optionCallback.contact === contact) {
    return vm.$store.dispatch('sipModule/call', [optionCallback.contact, optionCallback.queue, anonymizationEnabled])
  }

  const params = anonymizationEnabled
    ? [contact, null, anonymizationEnabled]
    : contact
  vm.$store.dispatch('sipModule/call', params)

  return item
}

export const startChat = (vm, destination, templateId, template, apiData = null) => {
  const queues = Array.isArray(template.queues) ? template.queues.map(({ queueId, queueName }) => ({ id: queueId, label: queueName })) : []
  if (!queues.length) return

  const areaCode = vm.$store.state.agentModule?.provision?.areaCode || ''
  const sourceId = toWhatsNumber(destination, areaCode)
  if (!sourceId) return notifyWarning('Número de telefone inválido')

  if (isLid(sourceId) && template.source !== 'WHATSAPPAPI') return notifyWarning('Contato sem número de telefone. Use um template WhatsApp API para iniciar a conversa')

  const matchesContact = (value) => Boolean(value) &&
    (sourceId.endsWith(value) || value.endsWith(destination) || destination.endsWith(value))

  const attendances = vm.$store.getters['attendanceModule/getAttendances']
  const attendance = attendances.find((item) => item.channel === 'TEXT' && item.botId === template.botId &&
    (matchesContact(item.sourceId) || matchesContact(item.phone)))
  const neverExpires = attendance?.source === 'WHATSAPPAPI' && hasContactReplied(attendance)
  const expiredTime = attendance && !neverExpires ? getChatExpired(attendance) : false

  if (attendance && !expiredTime) return router.push({ name: ROUTE_ATTENDANCE_ITEM, params: { attendanceId: attendance.id } }).catch(() => { })

  const agent = vm.$store.getters['agentModule/getAgent']
  const agentId = agent.id
  const templateButtons = template?.params?.buttons || template?.buttons || []
  const hasDynamicUrlButton = Array.isArray(templateButtons) && templateButtons.some(
    (btn) => btn?.type === 'url' && /\{\{[^}]*\}\}/.test(String(btn?.url || ''))
  )
  const hasParams = template?.params?.length || template?.params?.params?.length || hasDynamicUrlButton

  if (hasParams) {
    return vm.$q.dialog({
      parent: vm,
      template,
      agent,
      apiData,
      component: AskTemplateParams
    }).onOk(({ queueId, fields, header }) => {
      vm.$store.dispatch('socketModule/startChat', { templateId, sourceId, agentId, queueId, fields, header, type: 'start-attendance' })
    })
  }

  if (queues.length === 1) {
    const queueId = queues[0].id
    return vm.$store.dispatch('socketModule/startChat', { templateId, sourceId, agentId, queueId, header: template.header, type: 'start-attendance' })
  }

  return vm.$q.dialog({ parent: vm, template, apiData, component: AskTemplateQueue }).onOk(({ queueId }) => {
    vm.$store.dispatch('socketModule/startChat', { templateId, sourceId, agentId, queueId, header: template.header, type: 'start-attendance' })
  })
}

export const getFormatedNumber = async (item, areaCode) => {
  const callType = await getCallType(item, areaCode)
  item.callTypesName = String(callType || item.callTypesName || '').trim().toUpperCase()
  formatNationalNumber(item, areaCode)
  return item
}

export const getNationalFormatedNumber = ({ ...callCopy }, areaCode) => {
  const { contact } = callCopy

  const itemDDD = (contact.length === 10 || contact.length === 11)
    ? contact.slice(0, 2)
    : ''

  callCopy.callTypesName = itemDDD === areaCode ? 'LOCAL' : 'NATIONAL'

  formatNationalNumber(callCopy, areaCode)

  return callCopy
}

const formatNationalNumber = (item, areaCode) => {
  const canRemoveDDD = ['LOCAL', 'VC1'].includes(item.callTypesName)
  if (canRemoveDDD) item.contact = formatLocalNumber(item.contact, areaCode)

  if (item.callTypesName === 'NATIONAL' && item.contact.length === 11) {
    item.contact = `0${item.contact}`
    return item
  }

  const canAddZero = !canRemoveDDD && startWithDDD(toNationalNumber(item.contact))
  if (canAddZero) item.contact = `0${toNationalNumber(item.contact)}`

  return item
}

export const toWhatsNumber = (destination, areaCode) => {
  if (!destination) return null

  if (isLid(destination)) return String(destination).trim().toLowerCase()

  const isInternal = destination.length <= 7
  if (isInternal) return null

  const contact = toNationalNumber(destination)
  if (!contact) return null

  const isVC1 = ['9', '8'].includes(contact[0]) && [9, 8].includes(contact.length)
  if (isVC1 && contact.length === 9) return `55${areaCode}${contact}`
  if (isVC1 && contact.length === 8) return `55${areaCode}9${contact}`

  for (const ddd of ddds) {
    const ignoreDDD = !contact.startsWith(ddd) && !contact.startsWith(`0${ddd}`)
    if (ignoreDDD) continue

    if (contact.startsWith(`${ddd}9`) || contact.startsWith(`${ddd}8`)) {
      if (contact.length === 10) return `55${ddd}9${contact.substr(2)}`
      if (contact.length === 11) return `55${contact}`
    }

    if (contact.startsWith(`0${ddd}9`) || contact.startsWith(`0${ddd}8`)) {
      if (contact.length === 11) return `55${ddd}9${contact.substr(3)}`
      if (contact.length === 12) return `55${ddd}${contact.substr(3)}`
    }

    return contact.startsWith(`0${ddd}`) ? `55${ddd}${contact.substr(3)}` : `55${contact}`
  }

  return destination
}

const getCallType = async (attendance, areaCode) => {
  if (attendance.callTypesName) return attendance.callTypesName

  const nationalNumber = toNationalNumber(attendance.contact)
  const isMobile = nationalNumber.startsWith(`${areaCode}9`) || (nationalNumber[0] === '9' && nationalNumber.length === 9)
  if (isMobile) return 'VC1'

  const isOtherArea = !nationalNumber.startsWith(areaCode)
  if (isOtherArea) return null

  return api.loadAttendanceType(nationalNumber).then((res) => res?.typeContact)
}

const formatLocalNumber = (destination, areaCode) => {
  const nationalNumber = toNationalNumber(destination)
  return nationalNumber.startsWith(areaCode) ? nationalNumber.substr(2) : nationalNumber
}

export const toNationalNumber = (contact) => {
  if (contact.startsWith('0550')) return contact.substr(4)
  if (contact.startsWith('550')) return contact.substr(3)
  if (contact.startsWith('055')) return contact.substr(3)
  if (contact.startsWith('55')) return contact.substr(2)
  if (contact.startsWith('0')) return contact.substr(1)
  return contact
}

const ddds = ['11', '12', '13', '14', '15', '16', '17', '18', '19', '21', '22', '24', '27', '28', '31', '32', '33', '34', '35', '37', '38', '41', '42', '43', '44', '45', '46', '47', '48', '49', '51', '53', '54', '55', '61', '62', '64', '65', '66', '67', '82', '71', '73', '74', '75', '77', '85', '88', '98', '99', '83', '81', '87', '86', '89', '84', '79', '68', '96', '92', '97', '91', '93', '94', '69', '95', '63']
const startWithDDD = (contact) => {
  for (const ddd of ddds) {
    if (contact.startsWith(ddd)) {
      return true
    }
  }

  return false
}
